import { Button } from "@/components/ui/button";
import { Disc3, Plus, LogOut, Download, FileText, FileJson, Heart, FileBarChart2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImportCsvButton } from "@/components/ImportCsvButton";

async function downloadExport(format) {
    try {
        const res = await api.get(`/vinyls/export?format=${format}`, { responseType: "blob" });
        const disp = res.headers["content-disposition"] || "";
        const match = disp.match(/filename="([^"]+)"/);
        const filename = match ? match[1] : `vinyl-collection.${format}`;
        const blob = new Blob([res.data], {
            type: format === "csv" ? "text/csv" : "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
        toast.error("Export failed");
    }
}

export default function Header({ onAdd, tab, onTabChange, addLabel, onImported }) {
    const { logout } = useAuth();
    const navigate = useNavigate();
    return (
        <header
            className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border"
            data-testid="app-header"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 shrink-0">
                    <Disc3 className="w-7 h-7 text-primary vinyl-spin" strokeWidth={1.5} />
                    <div className="leading-none hidden sm:block">
                        <p className="font-display font-bold text-lg tracking-tight">Wax & Sleeves</p>
                        <p className="overline text-muted-foreground mt-1">Vinyl Archive</p>
                    </div>
                </div>

                <Tabs value={tab} onValueChange={onTabChange} className="flex-1 max-w-md">
                    <TabsList className="grid grid-cols-2 w-full" data-testid="header-tabs">
                        <TabsTrigger value="collection" data-testid="tab-collection">
                            <Disc3 className="w-3.5 h-3.5 mr-1.5" />
                            Collection
                        </TabsTrigger>
                        <TabsTrigger value="wishlist" data-testid="tab-wishlist">
                            <Heart className="w-3.5 h-3.5 mr-1.5" />
                            Wishlist
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="flex items-center gap-2 shrink-0">
                    {tab === "collection" && <ImportCsvButton onImported={onImported} />}
                    {tab === "collection" && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="gap-2 rounded-full"
                                    data-testid="export-btn"
                                >
                                    <Download className="w-4 h-4" />
                                    <span className="hidden sm:inline">Export</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => downloadExport("csv")} data-testid="export-csv-btn">
                                    <FileText className="w-4 h-4 mr-2" />
                                    Download CSV
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => downloadExport("json")} data-testid="export-json-btn">
                                    <FileJson className="w-4 h-4 mr-2" />
                                    Download JSON
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/report")} data-testid="insurance-report-btn">
                                    <FileBarChart2 className="w-4 h-4 mr-2" />
                                    Insurance report
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    <Button
                        onClick={onAdd}
                        className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                        data-testid="add-record-btn"
                    >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">{addLabel}</span>
                    </Button>
                    <Button
                        onClick={logout}
                        variant="ghost"
                        size="icon"
                        className="rounded-full text-muted-foreground hover:text-foreground"
                        title="Sign out"
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </header>
    );
}
